/**
 * 分级日志系统：支持级别、控制台染色、上下文（文件名/模块名）标签。
 * 使用方式：const log = createLogger('ChatPanel'); log.info('消息', data);
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const LEVEL_STYLES: Record<LogLevel, string> = {
  debug: 'color: #64748b; font-weight: normal;',
  info: 'color: #059669; font-weight: 600;',
  warn: 'color: #ea580c; font-weight: 700;',
  error:
    'color: #dc2626; font-weight: 700; background: rgba(220,38,38,0.12); padding: 1px 4px; border-radius: 2px;',
}

const TIME_STYLE = 'color: #94a3b8; font-weight: normal;'
const TAG_STYLE = 'color: #4f46e5; font-weight: 600;'
const MESSAGE_STYLE = 'color: inherit;'

function getMinLevel(): LogLevel {
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'production') {
    return (import.meta.env?.VITE_LOG_LEVEL as LogLevel) || 'info'
  }
  return (import.meta.env?.VITE_LOG_LEVEL as LogLevel) || 'debug'
}

let cachedMinLevel: number | null = null

function shouldLog(level: LogLevel): boolean {
  if (cachedMinLevel === null) {
    cachedMinLevel = LEVEL_ORDER[getMinLevel()]
  }
  return LEVEL_ORDER[level] >= cachedMinLevel
}

function timestamp(): string {
  const now = new Date()
  const h = String(now.getHours()).padStart(2, '0')
  const m = String(now.getMinutes()).padStart(2, '0')
  const s = String(now.getSeconds()).padStart(2, '0')
  const ms = String(now.getMilliseconds()).padStart(3, '0')
  return `${h}:${m}:${s}.${ms}`
}

function formatLevel(level: LogLevel): string {
  return level.toUpperCase().padEnd(5)
}

export interface Logger {
  debug(message: string, ...args: unknown[]): void
  info(message: string, ...args: unknown[]): void
  warn(message: string, ...args: unknown[]): void
  error(message: string, ...args: unknown[]): void
}

export function createLogger(context: string): Logger {
  const log = (level: LogLevel, message: string, ...args: unknown[]) => {
    if (!shouldLog(level)) return
    const time = timestamp()
    const levelLabel = formatLevel(level)
    const levelStyle = LEVEL_STYLES[level]
    const method = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log'
    ;(console as Console)[method](
      `%c${time} %c[${levelLabel}] %c[${context}] %c${message}`,
      TIME_STYLE,
      levelStyle,
      TAG_STYLE,
      MESSAGE_STYLE,
      ...args,
    )
  }

  return {
    debug: (message: string, ...args: unknown[]) => log('debug', message, ...args),
    info: (message: string, ...args: unknown[]) => log('info', message, ...args),
    warn: (message: string, ...args: unknown[]) => log('warn', message, ...args),
    error: (message: string, ...args: unknown[]) => log('error', message, ...args),
  }
}

export function resetLogLevelCache(): void {
  cachedMinLevel = null
}
